const TARGET_TICK_COUNT = 8;
const DENSE_TARGET_TICK_COUNT = 14;
const MAX_ALL_LABEL_POINTS = 14;
const MIN_TICK_LABEL_WIDTH_PX = 56;
const MIN_DENSE_TICK_LABEL_WIDTH_PX = 48;

const buildTickIndexes = (totalPoints: number, targetTickCount: number): Set<number> => {
  const tickIndexes = new Set<number>();
  if (totalPoints <= 0 || targetTickCount <= 1) return tickIndexes;

  const lastIndex = totalPoints - 1;
  const step = Math.max(1, Math.ceil(totalPoints / targetTickCount));
  for (let position = lastIndex; position >= 0; position -= step) {
    tickIndexes.add(position);
  }

  return tickIndexes;
};

const resolveWidthLimitedTargetTickCount = (
  totalPoints: number,
  targetTickCount: number,
  availableWidth: number | null | undefined,
  minTickLabelWidth: number,
): number => {
  if (
    typeof availableWidth !== 'number' ||
    !Number.isFinite(availableWidth) ||
    availableWidth <= 0
  ) {
    return targetTickCount;
  }

  const widthLimitedTickCount = Math.max(2, Math.floor(availableWidth / minTickLabelWidth));
  return Math.max(2, Math.min(totalPoints, targetTickCount, widthLimitedTickCount));
};

export const buildTickLabelInterval = (
  totalPoints: number,
  availableWidth?: number | null,
): ((value: string, index: number) => boolean) | 'auto' => {
  const targetTickCount = resolveWidthLimitedTargetTickCount(
    totalPoints,
    TARGET_TICK_COUNT,
    availableWidth,
    MIN_TICK_LABEL_WIDTH_PX,
  );
  if (totalPoints <= targetTickCount) return 'auto';
  const tickIndexes = buildTickIndexes(totalPoints, targetTickCount);

  return (_value: string, index: number) => tickIndexes.has(index);
};

export const buildAdaptiveTickLabelInterval = (
  totalPoints: number,
  preferAllLabels: boolean,
  availableWidth?: number | null,
): ((value: string, index: number) => boolean) | 'auto' => {
  if (!preferAllLabels) return buildTickLabelInterval(totalPoints, availableWidth);
  const targetTickCount = resolveWidthLimitedTargetTickCount(
    totalPoints,
    totalPoints <= MAX_ALL_LABEL_POINTS ? totalPoints : DENSE_TARGET_TICK_COUNT,
    availableWidth,
    MIN_DENSE_TICK_LABEL_WIDTH_PX,
  );
  if (totalPoints <= targetTickCount) return showEveryTickLabel;
  const tickIndexes = buildTickIndexes(totalPoints, targetTickCount);

  return (_value: string, index: number) => tickIndexes.has(index);
};

export const showEveryTickLabel = (): boolean => true;
